import { useState } from 'react';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import {
	Button,
	Dialog,
	InputField,
	Text,
	toast,
} from '@components';
import { BUTTON_TYPES, TItemType } from '@types';
import { withPlural } from '@utils';
import { useDeleteItemType } from '@api/RateCards/itemTypeApis';

export interface DeleteItemTypeButtonProps {
	itemType: TItemType;
	/** Called once the API has deleted it. */
	onDeleted: () => void;
}

/** Deletes the Item type once its name is typed to confirm. */
export const DeleteItemTypeButton = (props: DeleteItemTypeButtonProps) => {
	const { itemType, onDeleted } = props;

	const [confirming, setConfirming] = useState(false);
	const [typedName, setTypedName] = useState('');
	const laneCount = itemType.lane_count ?? 0;

	const { deletingItemType, callDeleteItemType } = useDeleteItemType();

	const startConfirming = () => {
		setTypedName('');
		setConfirming(true);
	}

	const deleteItemType = () => {
		callDeleteItemType({ routes: [itemType.id] })
			.then((res) => {
				// An unreachable server resolves with nothing rather than throwing.
				if (!res) {
					toast.error('Unable to delete the item type.');
					return;
				}
				toast.success('Item type deleted');
				setConfirming(false);
				onDeleted();
			})
			.catch((err) => {
				toast.error(err?.cause?.response?.data?.message || 'Unable to delete the item type.');
			});
	}

	return (
		<>
			<Button
				text={'Delete item type'}
				type={BUTTON_TYPES.TERTIARY}
				Icon={DeleteOutlinedIcon}
				onClick={startConfirming}
			/>
			<Dialog
				size={'small'}
				open={confirming}
				onClose={() => setConfirming(false)}
				title={'Delete item type'}
				text={'Are you sure you want to delete this item type?'}
				actionButtonText={'Delete item type'}
				actionButtonFn={deleteItemType}
				actionButtonDisabled={typedName.trim() !== itemType.name}
				actionButtonLoading={deletingItemType}
				closeOnButtonAction={false}
				slot={
					<>
						{laneCount > 0 && <Text>
							It is currently used by {laneCount} {withPlural('lane', laneCount)}.<br />
							Those lanes will continue to use this item type, even when it is removed.
						</Text>}
						<InputField
							label={`Type "${itemType.name}" to confirm`}
							name={'confirm_name'}
							value={typedName}
							onChange={(e) => setTypedName(e.target.value)}
							autoComplete={'off'}
						/>
					</>
				}
			/>
		</>
	);
}
